import { canAccessGuides, canWriteJobsAndCompanies, isHrRole, isSuperAdmin } from '@components/Common/Utils/roles';
import type { PaletteEntity, PaletteRecord } from './paletteSearch';

/**
 * Non-record palette rows: commands and go-to navigation.
 *
 * Every entry carries the roles that may see it. The palette is a shortcut to
 * things the user can already do, so it must never surface a destination their
 * role would bounce them out of.
 */

export interface PaletteAction {
  id: string;
  title: string;
  subtitle: string;
  path: string;
  /** Right-aligned type hint. */
  hint: string;
  /** When false, the row is hidden for this user. */
  visible: (ctx: PaletteContext) => boolean;
}

/**
 * The palette is a second way to reach every page, so its visibility rules have to match the
 * sidebar's exactly — a gap here is navigable by keyboard even when the link is hidden.
 *
 * The context carried a single `isHr` boolean, which could not express a third role: `!isHr` made
 * Marketing a Super Admin here just as it did everywhere else.
 */
export interface PaletteContext {
  role?: string | null;
}

const everyone = () => true;
const superAdminOnly = ({ role }: PaletteContext) => isSuperAdmin(role);
const hrOnly = ({ role }: PaletteContext) => isHrRole(role);
/** Guides: the one destination Marketing may reach. */
const guidesAccess = ({ role }: PaletteContext) => canAccessGuides(role);
/** Creating jobs and companies: everyone but Marketing. */
const recordWriters = ({ role }: PaletteContext) => canWriteJobsAndCompanies(role);

/**
 * Commands. `match` decides whether the command is relevant to what has been
 * typed, so "Add Job" surfaces on "job" or "add" but not on every keystroke.
 */
export const COMMANDS: (PaletteAction & { match: string[] })[] = [
  {
    id: 'cmd-add-job',
    title: 'Add Job',
    subtitle: 'Opens the job form',
    path: '/jobs/new',
    hint: 'Command',
    match: ['add', 'new', 'job', 'create', 'post'],
    visible: recordWriters,
  },
  {
    id: 'cmd-add-company',
    title: 'Add Company',
    subtitle: 'Opens the company form',
    path: '/companies/new',
    hint: 'Command',
    match: ['add', 'new', 'company', 'create'],
    visible: recordWriters,
  },
  {
    id: 'cmd-assign-hr',
    title: 'Assign dedicated HR',
    subtitle: 'Premium Hiring clients and their recruiter workload',
    path: '/hr',
    hint: 'Command',
    match: ['assign', 'hr', 'recruiter', 'premium', 'managed', 'dedicated'],
    visible: superAdminOnly,
  },
  {
    id: 'cmd-import-seekers',
    title: 'Import job seekers',
    subtitle: 'Bulk upload with column mapping · desktop only',
    path: '/jobseekers/import',
    hint: 'Command',
    match: ['import', 'upload', 'bulk', 'seeker', 'candidate'],
    visible: superAdminOnly,
  },
  {
    id: 'cmd-my-accounts',
    title: 'Open Premium Hiring Clients',
    subtitle: 'The Premium Hiring clients assigned to you',
    path: '/my-accounts',
    hint: 'Command',
    match: ['my', 'accounts', 'premium', 'managed', 'clients', 'assigned'],
    visible: hrOnly,
  },
];

/** Go-to navigation. Always available; filtered by title match. */
export const GO_TO: PaletteAction[] = [
  {
    id: 'go-guides',
    title: 'Guides',
    subtitle: 'Import, edit and publish guides',
    path: '/guides',
    hint: 'Go to',
    // The palette had no guides destination at all, so Marketing would have opened it to find
    // nothing they could reach.
    visible: guidesAccess,
  },
  { id: 'go-dashboard',    title: 'Dashboard → Overview',     subtitle: 'KPIs and pipeline health', path: '/dashboard/overview',        hint: 'Go to', visible: everyone },
  { id: 'go-applications', title: 'Applications → List',      subtitle: 'The full applications table', path: '/applications/list',      hint: 'Go to', visible: everyone },
  { id: 'go-board',        title: 'Applications → Board',     subtitle: 'Stage-by-stage pipeline',  path: '/applications/board',        hint: 'Go to', visible: everyone },
  { id: 'go-jobs',         title: 'Jobs',                     subtitle: 'Every tracked demand',     path: '/jobs',                      hint: 'Go to', visible: everyone },
  { id: 'go-companies',    title: 'Companies',                subtitle: 'Client companies',         path: '/companies',                 hint: 'Go to', visible: everyone },
  { id: 'go-jobseekers',   title: 'Job Seekers',              subtitle: 'Candidate database',       path: '/jobseekers',                hint: 'Go to', visible: everyone },
  { id: 'go-employers',    title: 'Employers',                subtitle: 'Employer accounts',        path: '/employers',                 hint: 'Go to', visible: everyone },
  { id: 'go-hr',           title: 'HR → Premium Hiring Clients', subtitle: 'Assignment and workload',  path: '/hr',                        hint: 'Go to', visible: superAdminOnly },
  { id: 'go-my-accounts',  title: 'Premium Hiring Clients',   subtitle: 'Your assigned clients',    path: '/my-accounts',               hint: 'Go to', visible: hrOnly },
  { id: 'go-email',        title: 'Email Campaigns → Emails', subtitle: 'Every email, and whether it is healthy', path: '/email-campaigns/emails', hint: 'Go to', visible: superAdminOnly },
  { id: 'go-ambassadors',  title: 'HR Ambassadors',           subtitle: 'External referrers, their candidates and rewards', path: '/ambassadors', hint: 'Go to', visible: superAdminOnly },
  { id: 'go-rbac',         title: 'User RBAC',                subtitle: 'Internal users and roles', path: '/user-rbac',                 hint: 'Go to', visible: superAdminOnly },
  { id: 'go-profile',      title: 'Your profile',             subtitle: 'Name, password and theme', path: '/user-details',            hint: 'Go to', visible: everyone },
  { id: 'go-notifications', title: 'Notifications',           subtitle: 'Platform events',          path: '/notifications',             hint: 'Go to', visible: everyone },
];

/* ── Recents ───────────────────────────────────────────────────────────────
 * The empty state shows the last records this user opened, so ⌘K → Enter is
 * a one-keystroke return to what they were working on.
 * ------------------------------------------------------------------------ */

const RECENTS_KEY = 'cc.palette.recents';
const MAX_RECENTS = 5;

export function readRecents(): PaletteRecord[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENTS) : [];
  } catch {
    return [];
  }
}

export function pushRecent(record: PaletteRecord): void {
  try {
    const next = [record, ...readRecents().filter((r) => r.path !== record.path)].slice(0, MAX_RECENTS);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // A full or unavailable localStorage must never break navigation.
  }
}

/**
 * Entities the palette searches.
 *
 * All five list routes are open to both roles — what differs is which records
 * come back, and that is decided server-side. Commands and go-to rows are the
 * things that need role gating, and they carry their own `visible` predicate.
 */
export const SEARCHABLE_ENTITIES: PaletteEntity[] = [
  'job',
  'company',
  'jobseeker',
  'employer',
  'application',
];
